import React, { useMemo, useEffect, useCallback, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { QtyInput } from "./QtyInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  ClipboardList, AlertTriangle, CheckCircle2, ArrowDownToLine, Wand2,
  Save, Plus, Trash2, Sparkles, FileText, CheckCircle2 as CheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import { buildUnitSchedule } from "../../lib/unitSchedule";
import { withVariantTags } from "../../lib/variantTags";
import { specForUnitModel } from "../../data/orderingCodes";
import {
  buildBaseCode, buildAccessoryCode,
} from "../../lib/orderingCodeBuilder";
import { exportConfigFileCSV, exportConfigFileXLSX } from "../../lib/scheduleExport";
import { generateSubmittalPDF } from "../../lib/pdfReport";
import { uploadProjectReport } from "../../lib/reportsApi";

// ----------------------------------------------------------------------------
// Schedule tab (formerly Assignment).
//
// Slim 7-column layout:
//   # | Room | Unit Model | QTY | Config variant | Base Code (B) | Accessory Code (A)
//
// One row per (room, assigned variant). For multi-unit rooms with mixed
// configs, multiple rows render — same room name, distinct variants.
// Each row exposes:
//   • Qty input (editable; sum across rows for one room must equal room qty)
//   • Config variant dropdown
//   • Live B / A codes
// "+ Add split" button per room when row sums < room qty.
// ----------------------------------------------------------------------------

// Must match ConfiguratorTab.groupKey exactly.
//   v20.7-: "specId__length"                          (2 parts)
//   v20.8:  "specId__length__circuits"                (3 parts)
//   v20.9+: "unitModel__specId__length__circuits"     (4 parts — current,
//           fixes Model C vs C1 (and S/SHW, O-1/O-2, I-*, L-*) key collision)
// Without unit_model here, AssignmentTab's variantsByGroupKey map can't find
// the configurations the user defined in the Configurator, and every row
// shows "no variants" + "0 of N assigned".
const groupKey = (g) => {
  const specId = specForUnitModel(g.unit_model)?.id || g.unit_model;
  return `${g.unit_model}__${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
};
const isLengthAgnostic = (m) => m === "Model N" || m === "Model N-f";

// Helper: return true when assignment `a` matches the given room identity.
// Prefers room_id (schema v3). Falls back to room_idx for older payloads.
function matchesRoom(a, room) {
  if (room.room_id != null && a.room_id != null) return a.room_id === room.room_id;
  return a.room_idx === room.room_idx; // backward-compat
}

// Build a new assignment object with the correct identity fields.
// Writes room_id as the primary key; also writes room_idx for the
// transition period so the backend's v2→v3 migrator has both to work with.
function makeAssignment(room, qty) {
  const base = { qty };
  if (room.room_id != null) base.room_id = room.room_id;
  if (room.room_idx != null) base.room_idx = room.room_idx; // transition compat
  return base;
}

// Mutate configurations[] by setting a single (room, variant) assignment.
// `room` is the room entry from g.rooms — carries both room_id and room_idx.
function setRoomVariantQty(configurations, groupK, room, variantId, qty) {
  const safeQty = Math.max(0, parseInt(qty, 10) || 0);
  return configurations.map((c) => {
    if (c.key !== groupK) return c;
    const others = (c.assignments || []).filter((a) => !matchesRoom(a, room));
    if (c.id === variantId) {
      if (safeQty <= 0) return { ...c, assignments: others };
      return { ...c, assignments: [...others, makeAssignment(room, safeQty)] };
    }
    return c;
  });
}

// Move ALL room qty to one variant exclusively (for "Apply to all" macros).
function exclusivelyAssignRoom(configurations, groupK, room, roomQty, variantId) {
  return configurations.map((c) => {
    if (c.key !== groupK) return c;
    const others = (c.assignments || []).filter((a) => !matchesRoom(a, room));
    if (c.id === variantId) {
      return { ...c, assignments: [...others, makeAssignment(room, roomQty)] };
    }
    return { ...c, assignments: others };
  });
}

function unitModelLabel(g, isSI = false) {
  if (isLengthAgnostic(g.unit_model)) return g.unit_model;
  if (g.length_ft == null) return g.unit_model;
  const lenStr = isSI
    ? `${Math.round(parseFloat(g.length_ft) * 300)} mm`
    : `${g.length_ft} ft`;
  return `${g.unit_model} · ${lenStr}`;
}

export default function AssignmentTab({
  project, configurations, setConfigurations, rooms, results, isSI = false, onUpdateRoomQty, onSave,
  configReport = null, onReportGenerated,
}) {
  const groups = useMemo(
    () => buildUnitSchedule(rooms || [], results || []),
    [rooms, results]
  );
  const tagged = useMemo(
    () => withVariantTags(configurations || [], groups),
    [configurations, groups]
  );

  // Index variants under the NEW 4-part group key per group, regardless of
  // whether the saved cfg.key is still in an old 2- or 3-part format. Saved
  // projects from before v20.9 carry old-format keys; ConfiguratorTab's
  // sync effect migrates them on visit, but a user may land directly on the
  // Assignment tab before that runs — without this dual lookup, every row
  // shows "no variants" until the user opens Configurator first.
  //
  // Two-pass matching:
  //   1. Exact new-format match first (unambiguous).
  //   2. Legacy 3-part / 2-part match for variants not claimed in pass 1.
  //      Ambiguous legacy keys (e.g., "C__1200__2" matches BOTH Model C and
  //      Model C1 groups) get first-write-wins — same semantics as the
  //      ConfiguratorTab sync effect's groupByLegacyKey.
  const variantsByGroupKey = useMemo(() => {
    const m = new Map();
    const claimed = new Set();
    // Pass 1: exact new-format match.
    groups.forEach((g) => {
      const newKey = groupKey(g);
      const variantsHere = tagged.filter((v) => v.key === newKey && !claimed.has(v.id));
      variantsHere.forEach((v) => claimed.add(v.id));
      if (variantsHere.length) m.set(newKey, variantsHere);
    });
    // Pass 2: legacy-key fallback for unclaimed groups.
    groups.forEach((g) => {
      const newKey = groupKey(g);
      if (m.has(newKey)) return;
      const specId = specForUnitModel(g.unit_model)?.id || g.unit_model;
      const v208 = `${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
      const v207 = `${specId}__${g.length_ft ?? ""}`;
      const matchKeys = new Set([v208, v207]);
      const variantsHere = tagged.filter((v) => matchKeys.has(v.key) && !claimed.has(v.id));
      variantsHere.forEach((v) => claimed.add(v.id));
      if (variantsHere.length) m.set(newKey, variantsHere);
    });
    return m;
  }, [tagged, groups]);

  const [savingBusy, setSavingBusy] = useState(false);
  const [configReportBusy, setConfigReportBusy] = useState(false);

  const handleGenerateConfigReport = useCallback(async () => {
    if (!project?.id) {
      toast.error("Save the project first so the report can be persisted.");
      return;
    }
    setConfigReportBusy(true);
    try {
      const blob = await generateSubmittalPDF({
        project, rooms, results, configurations, isSI,
        includeConfigSchedule: true,
        configReportOnly: true,
        returnBlob: true,
      });
      const safeName = (project?.name || "Project").replace(/[^a-zA-Z0-9-]/g, "_");
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `ZAMBA_${safeName}_ConfigReport_${stamp}.pdf`;
      // Trigger local download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
      // Persist
      await uploadProjectReport(project.id, "config", blob, filename);
      toast.success("Config Report generated & saved");
      if (onReportGenerated) onReportGenerated("config");
    } catch (e) {
      toast.error("Config Report failed: " + (e.message || "unknown"));
    } finally {
      setConfigReportBusy(false);
    }
  }, [project, rooms, results, configurations, isSI, onReportGenerated]);

  const handleSave = useCallback(async () => {
    // "Save Config File" — exports the variant summary (Tag / Variant /
    // Unit Model / QTY / Code B / Code A) as Excel + CSV. The button used
    // to write the project to the DB; that behavior is now in the Summary
    // tab's "Save Schedule" button.
    setSavingBusy(true);
    try {
      await exportConfigFileXLSX({ project, rooms, results, configurations, isSI });
      exportConfigFileCSV({ project, rooms, results, configurations, isSI });
      toast.success("Config file exported (XLSX + CSV)");
    } catch (e) {
      toast.error("Failed to export config file: " + (e.message || "unknown"));
    } finally {
      setSavingBusy(false);
    }
  }, [project, rooms, results, configurations, isSI]);

  // Build the flat list of rows: one row per (room, variant) assignment.
  // Unassigned rooms get one row with variant=null.
  const flatRows = useMemo(() => {
    const out = [];
    let rowNum = 0;
    groups.forEach((g) => {
      const k = groupKey(g);
      const cands = variantsByGroupKey.get(k) || [];
      g.rooms.forEach((r) => {
        rowNum += 1;
        const cells = cands
          .map((v) => {
            // Prefer room_id (schema v3); fall back to room_idx.
            const a = (v.assignments || []).find((x) => matchesRoom(x, r));
            return { variant: v, qty: parseInt(a?.qty, 10) || 0 };
          })
          .filter((x) => x.qty > 0);
        if (cells.length === 0) {
          out.push({
            num: rowNum,
            subRow: 0,
            isFirstSubRow: true,
            group: g,
            groupKey: k,
            room: r,
            variant: null,
            qty: 0,
            cands,
          });
        } else {
          cells.forEach((c, idx) => {
            out.push({
              num: rowNum,
              subRow: idx,
              isFirstSubRow: idx === 0,
              group: g,
              groupKey: k,
              room: r,
              variant: c.variant,
              qty: c.qty,
              cands,
            });
          });
        }
      });
    });
    return out;
  }, [groups, variantsByGroupKey]);

  // Ref to distinguish user-initiated room.qty changes (Schedule tab edit)
  // from engine-driven changes (runRoomSelection result merge). Only when the
  // user edits the Schedule should the reconcile effect be allowed to fire.
  // Fix #4: gates the auto-reconcile so engine-driven qty updates do not undo
  // a manual Schedule edit.
  const userInitiatedQtyChangeRef = React.useRef(false);

  // Fix H1-reconcile: tracks room keys where setRowVariant just assigned a
  // partial qty (targetQty < room.qty) to start a split. The reconcile effect
  // must NOT "heal" those rooms back to room.qty — the partial assignment is
  // intentional. Consumed (cleared) on the next effect run, exactly like
  // userInitiatedQtyChangeRef.
  const pendingSplitRoomKeysRef = React.useRef(new Set());

  // Per-row mutators — all use the functional updater form of setConfigurations
  // so they always operate on the latest state, not a captured-at-render slice.
  const setRowQty = useCallback((row, qty) => {
    if (!row.variant) return; // Unassigned row → must pick a variant first
    const { groupKey: gk, room, variant } = row;

    // Fix #2: compute the side-effect signal OUTSIDE the updater so no
    // side effects run inside the functional updater body. React's StrictMode
    // double-invokes updaters; a side-effect inside would fire twice.
    //
    // We derive the post-update total from the current configurations snapshot
    // (captured here in render scope) rather than from the updater's `prev`.
    // This is sound because:
    //   (a) setConfigurations is the only writer to this slice of state.
    //   (b) We read before calling it, so we apply the same delta manually.
    let pendingQtyUpdate = null;
    if (onUpdateRoomQty) {
      // Compute what the configs will look like after the update (pure, no side-effects)
      const previewConfigs = setRoomVariantQty(configurations || [], gk, room, variant.id, qty);
      const variantsInRoom = previewConfigs
        .filter((c) => c.key === gk)
        .filter((c) => (c.assignments || []).some((a) => matchesRoom(a, room) && a.qty > 0));
      const totalAssigned = variantsInRoom.reduce((s, c) => {
        const a = (c.assignments || []).find((x) => matchesRoom(x, room));
        return s + ((a && a.qty) || 0);
      }, 0);
      // SINGLE-VARIANT INVARIANT: if this room only has one distinct variant
      // assigned, sync room.qty (plan.qty) to the total assignment sum so the
      // Summary tab's Qty stays in lock-step with the Schedule tab.
      // Suppressed while a split is ACTIVE: multiple variants already assigned
      // to this room, OR the total is below room.qty while multiple candidate
      // variants exist (distribution still in progress). Single-variant rooms
      // always propagate so plan.qty stays in sync with the schedule edit.
      const previewTotalAssigned = previewConfigs
        .filter((c) => c.key === gk)
        .reduce((s, c) => {
          const a = (c.assignments || []).find((x) => matchesRoom(x, room));
          return s + ((a && a.qty > 0 ? a.qty : 0));
        }, 0);
      const splitActive =
        variantsInRoom.length > 1 ||
        (previewTotalAssigned < room.qty && row.cands.length > 1);
      if (!splitActive && variantsInRoom.length === 1 && totalAssigned > 0 && totalAssigned !== room.qty) {
        pendingQtyUpdate = { roomIdx: room.room_idx, qty: totalAssigned };
      }
    }

    // Mark the upcoming room.qty change as user-initiated so the reconcile
    // effect knows it should NOT undo this edit (Fix #4).
    if (pendingQtyUpdate) {
      userInitiatedQtyChangeRef.current = true;
    }

    // Commit the pure state update (no side effects inside the updater).
    setConfigurations((prev) =>
      setRoomVariantQty(prev || [], gk, room, variant.id, qty)
    );

    // Dispatch the room.qty side-effect AFTER committing the state update.
    // queueMicrotask ensures this runs after React has batched the setState
    // but before the next paint — keeps it in the same event-loop tick as
    // the setState without being inside the updater.
    if (pendingQtyUpdate) {
      queueMicrotask(() => {
        onUpdateRoomQty(pendingQtyUpdate.roomIdx, pendingQtyUpdate.qty);
      });
    }
  }, [setConfigurations, onUpdateRoomQty, configurations]);

  const setRowVariant = useCallback((row, variantId) => {
    // Capture the stable read-only properties we need from `row` before the
    // updater function runs asynchronously (row is from render, safe to read).
    const { groupKey: gk, room, variant: oldVariant, qty } = row;
    // First-variant assignment: when room.qty > 1 and at least 2 variants exist,
    // assign only 1 unit so remaining > 0 and "+ Split" becomes reachable.
    // Changing an already-assigned variant keeps the current row qty unchanged.
    const targetQty = oldVariant
      ? qty
      : (row.cands.length > 1 && room.qty > 1 ? 1 : room.qty);
    // Fix H1-reconcile: if this is a partial first-variant assignment (split
    // intent), mark the room key so the reconcile effect skips healing it.
    // Key uses room_id when available (schema v3), otherwise room_idx.
    const roomKey = room.room_id != null
      ? `${gk}__${room.room_id}`
      : `${gk}__${room.room_idx}`;
    if (!oldVariant && targetQty < room.qty) {
      pendingSplitRoomKeysRef.current.add(roomKey);
    }
    setConfigurations((prev) => {
      let next = prev || [];
      // First, clear the previous (room → old variant) qty if any
      if (oldVariant) {
        next = setRoomVariantQty(next, gk, room, oldVariant.id, 0);
      }
      // Then set the new (room → new variant) qty.
      next = setRoomVariantQty(next, gk, room, variantId, targetQty);
      return next;
    });
  }, [setConfigurations]);

  const removeRow = useCallback((row) => {
    if (!row.variant) return;
    const { groupKey: gk, room, variant } = row;
    setConfigurations((prev) =>
      setRoomVariantQty(prev || [], gk, room, variant.id, 0)
    );
  }, [setConfigurations]);

  // "Add split row" — picks the first unused variant in this room and assigns
  // the remaining units to it.
  //
  // Gate conditions (mirrored in `canSplit` at render time — this is the
  // runtime safety net):
  //   (a) row.variant === null  → user hasn't picked a variant yet; don't
  //       auto-assign or the empty row visually "disappears".
  //   (b) row.cands.length === 1 → only one variant exists for this group;
  //       there's nothing to split into.
  //   (c) No free variant left  → all defined variants already used in room.
  //
  // sumSoFar and used are re-derived inside the functional updater from `prev`
  // to avoid the rare reorder-edge race where flatRows is stale vs. state.
  const addSplitRow = useCallback((row) => {
    if (!row.variant) {
      toast.error("Pick a variant for this row first, then click + Split to assign the remaining unit(s) to a different variant.");
      return;
    }
    if (row.cands.length <= 1) {
      toast.error("This group has only one variant. Go to Configurator → Add Variant to split this room across multiple configurations.");
      return;
    }
    // Capture stable ids from the current render before the updater runs.
    const { groupKey: gk, room, cands } = row;
    const roomQty = room.qty;
    setConfigurations((prev) => {
      const safePrev = prev || [];
      // Derive which variant ids already have qty>0 for this room from prev
      // (authoritative state — avoids stale flatRows race).
      const assignedVariantIds = new Set(
        safePrev
          .filter((c) => c.key === gk && (c.assignments || []).some((a) => matchesRoom(a, room) && a.qty > 0))
          .map((c) => c.id)
      );
      const free = cands.find((v) => !assignedVariantIds.has(v.id));
      if (!free) return safePrev; // all variants already in use — no-op
      const sumSoFar = safePrev
        .filter((c) => c.key === gk)
        .reduce((s, c) => {
          const a = (c.assignments || []).find((x) => matchesRoom(x, room));
          return s + (a?.qty || 0);
        }, 0);
      const remaining = Math.max(1, roomQty - sumSoFar);
      return setRoomVariantQty(safePrev, gk, room, free.id, remaining);
    });
  }, [setConfigurations]);

  // Clone the first (and only) variant in a group so the user can split a
  // multi-unit room across two distinct configurations without going back to
  // Configurator. The clone inherits the same base / accessory / extras but
  // gets a new id and an auto-incremented name so it appears as a second
  // option in the variant dropdown.
  const cloneOnlyVariant = useCallback((group) => {
    const k = groupKey(group);
    const cands = variantsByGroupKey.get(k) || [];
    if (cands.length !== 1) return;
    const src = cands[0];
    const newId = `${src.id}_clone_${Date.now()}`;
    const newVariant = {
      ...src,
      id: newId,
      name: src.name && src.name !== "Default" ? `${src.name} (2)` : "Variant 2",
    };
    setConfigurations((prev) => {
      const existing = prev || [];
      // Find the source config object and insert the clone right after it.
      const srcIdx = existing.findIndex((c) => c.id === src.id);
      if (srcIdx < 0) return [...existing, newVariant];
      return [
        ...existing.slice(0, srcIdx + 1),
        newVariant,
        ...existing.slice(srcIdx + 1),
      ];
    });
    toast.success("Variant cloned — you can now split this room across two variants.");
  }, [variantsByGroupKey, setConfigurations]);

  // Apply all rooms in a group to one specific variant.
  // `tagged` is stable per render; we look up the tag label before the updater.
  const applyToAllInGroup = useCallback((group, variantId) => {
    const k = groupKey(group);
    const v = tagged.find((x) => x.id === variantId);
    setConfigurations((prev) => {
      let next = prev || [];
      group.rooms.forEach((r) => {
        next = exclusivelyAssignRoom(next, k, r, r.qty, variantId);
      });
      return next;
    });
    toast.success(`All ${group.rooms.length} room(s) assigned to ${v?.tag || "variant"}`);
  }, [setConfigurations, tagged]);

  const canProjectApplyAll = useMemo(() => {
    if (!groups.length) return false;
    return groups.every((g) => (variantsByGroupKey.get(groupKey(g)) || []).length === 1);
  }, [groups, variantsByGroupKey]);

  const projectApplyAll = useCallback(() => {
    setConfigurations((prev) => {
      let next = prev || [];
      groups.forEach((g) => {
        const cands = variantsByGroupKey.get(groupKey(g)) || [];
        if (cands.length !== 1) return;
        const target = cands[0];
        g.rooms.forEach((r) => {
          next = exclusivelyAssignRoom(next, groupKey(g), r, r.qty, target.id);
        });
      });
      return next;
    });
    toast.success("All rooms auto-assigned to their default configuration");
  }, [setConfigurations, groups, variantsByGroupKey]);

  // ============ Bulk-fill Unassigned ============
  // Unassigned rows that need a variant. Each entry includes its applicable
  // variants (`cands`) so the matrix-modal can render only valid options per
  // row. A row whose group has exactly one variant gets auto-populated; rows
  // whose group has multiple variants are surfaced in a modal where the user
  // picks one variant per room.
  const unassignedRows = useMemo(
    () => flatRows.filter((r) => !r.variant && r.isFirstSubRow),
    [flatRows]
  );

  const [bulkOpen, setBulkOpen] = useState(false);
  // key: groupKey__roomId (or groupKey__roomIdx fallback) → variantId
  const [bulkSelections, setBulkSelections] = useState({});

  // Stable room key for bulk-fill dictionary — prefer room_id (schema v3).
  const roomBulkKey = (row) =>
    row.room.room_id != null
      ? `${row.groupKey}__${row.room.room_id}`
      : `${row.groupKey}__${row.room.room_idx}`;

  // Seed the bulk-fill selections dict for multi-variant unassigned rows.
  // Default each room to the variant that already has the most rooms in the
  // group (most popular variant), falling back to the first candidate.
  const seedBulkSelections = useCallback((multiVariantRows) => {
    // Count existing assignments per variant per group so we can pick the
    // most-popular variant as the sensible default for unassigned rooms.
    const popularByGroup = new Map(); // groupKey → variantId with most assigned rooms
    groups.forEach((g) => {
      const k = groupKey(g);
      const cands = variantsByGroupKey.get(k) || [];
      if (cands.length <= 1) return;
      const counts = cands.map((v) => ({
        id: v.id,
        count: (v.assignments || []).length,
      }));
      counts.sort((a, b) => b.count - a.count);
      popularByGroup.set(k, counts[0]?.id || cands[0]?.id);
    });

    const seed = {};
    multiVariantRows.forEach((row) => {
      const popular = popularByGroup.get(row.groupKey);
      // Use the most popular variant if it exists in this row's candidates,
      // otherwise fall back to the first candidate.
      const defaultId = row.cands.find((v) => v.id === popular)
        ? popular
        : row.cands[0]?.id;
      seed[roomBulkKey(row)] = defaultId || "";
    });
    return seed;
  }, [groups, variantsByGroupKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-assign single-variant rooms silently on mount and whenever
  // unassignedRows changes (e.g. after results arrive or configs update).
  // Multi-variant rooms open the picker modal instead — but only once per
  // "session" so the user isn't pestered again after dismissing it.
  const autoAssignFiredRef = React.useRef(false);
  useEffect(() => {
    if (!unassignedRows.length) return;

    const singleVariantRows = unassignedRows.filter((row) => row.cands.length === 1);
    const multiVariantRows  = unassignedRows.filter((row) => row.cands.length > 1);

    // Silently assign every room whose group has exactly one variant.
    if (singleVariantRows.length > 0) {
      setConfigurations((prev) => {
        let next = prev || [];
        singleVariantRows.forEach((row) => {
          next = exclusivelyAssignRoom(next, row.groupKey, row.room, row.room.qty, row.cands[0].id);
        });
        return next;
      });
    }

    // Open the multi-variant picker once automatically (first render where
    // multi-variant unassigned rooms appear). The user can dismiss it and
    // re-open via the "Resolve multi-variant assignments" button.
    if (multiVariantRows.length > 0 && !autoAssignFiredRef.current && !bulkOpen) {
      autoAssignFiredRef.current = true;
      setBulkSelections(seedBulkSelections(multiVariantRows));
      setBulkOpen(true);
    }
  // Run when the set of unassigned rows changes. `configurations` intentionally
  // omitted — setConfigurations is stable and we don't want a feedback loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassignedRows]);

  const openBulkFill = useCallback(() => {
    const multiVariantRows = unassignedRows.filter((row) => row.cands.length > 1);

    if (!multiVariantRows.length) {
      toast.message("No multi-variant rooms to resolve.");
      return;
    }

    // Re-seed with sensible defaults each time the user manually opens the modal.
    setBulkSelections(seedBulkSelections(multiVariantRows));
    setBulkOpen(true);
  }, [unassignedRows, seedBulkSelections]);

  // Apply matrix selections (each row → its picked variantId) and close modal.
  const applyBulkFill = useCallback(() => {
    // Pre-compute stable lookup data from current render before the updater runs.
    const entries = Object.entries(bulkSelections).filter(([, variantId]) => !!variantId);
    const rowLookups = entries.map(([key, variantId]) => {
      const row = unassignedRows.find((r) => roomBulkKey(r) === key);
      return row ? { gk: row.groupKey, room: row.room, roomQty: row.room.qty, variantId } : null;
    }).filter(Boolean);

    setConfigurations((prev) => {
      let next = prev || [];
      rowLookups.forEach(({ gk, room, roomQty, variantId }) => {
        next = exclusivelyAssignRoom(next, gk, room, roomQty, variantId);
      });
      return next;
    });
    setBulkOpen(false);
    if (rowLookups.length) toast.success(`Assigned ${rowLookups.length} room(s).`);
  }, [bulkSelections, unassignedRows, setConfigurations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group the multi-variant unassigned rows by groupKey for the modal layout.
  const multiVariantGroupsForModal = useMemo(() => {
    const rows = Object.keys(bulkSelections).map((key) =>
      unassignedRows.find((r) => roomBulkKey(r) === key)
    ).filter(Boolean);

    // Deduplicate by groupKey while preserving order.
    const seen = new Set();
    const groupKeys = [];
    rows.forEach((row) => {
      if (!seen.has(row.groupKey)) {
        seen.add(row.groupKey);
        groupKeys.push(row.groupKey);
      }
    });

    return groupKeys.map((gk) => ({
      gk,
      group: rows.find((r) => r.groupKey === gk)?.group,
      rows: rows.filter((r) => r.groupKey === gk),
    }));
  }, [bulkSelections, unassignedRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-row sum for validation.
  // Key: groupKey__roomId (schema v3 primary) or groupKey__roomIdx (fallback).
  const roomSumKey = (r) =>
    r.room.room_id != null
      ? `${r.groupKey}__${r.room.room_id}`
      : `${r.groupKey}__${r.room.room_idx}`;

  const roomSums = useMemo(() => {
    const m = new Map();
    flatRows.forEach((r) => {
      const key = roomSumKey(r);
      m.set(key, (m.get(key) || 0) + r.qty);
    });
    return m;
  }, [flatRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalRooms = groups.reduce((s, g) => s + g.rooms.length, 0);
  const okRooms = useMemo(() => {
    let n = 0;
    groups.forEach((g) => {
      const k = groupKey(g);
      g.rooms.forEach((r) => {
        // Use the same key scheme as roomSums.
        const key = r.room_id != null ? `${k}__${r.room_id}` : `${k}__${r.room_idx}`;
        if (roomSums.get(key) === r.qty) n += 1;
      });
    });
    return n;
  }, [groups, roomSums]);
  const allAssigned = totalRooms > 0 && okRooms === totalRooms;
  const unassigned = totalRooms - okRooms;

  // Auto-reconcile: when a room's qty changes (typically from SummaryTab) and
  // it has EXACTLY ONE variant already assigned to it, silently bump that
  // variant's assignment to the new room.qty. To avoid undoing manual
  // Schedule edits (regression from the previous round), we ONLY bump when:
  //   1) The room.qty changed from the value we last saw, AND
  //   2) The current assignment still equals that OLD room.qty (so the user
  //      only touched Summary, not Schedule), OR
  //   3) We've never observed this room before AND assigned !== r.qty
  //      (catches stale saved projects on initial load).
  // If the user reduced the assignment manually in Schedule, prevRoomQty ===
  // r.qty (Summary unchanged) so we leave the manual edit alone.
  //
  // Fix #4: also gate on userInitiatedQtyChangeRef. When room.qty changes
  // due to the engine returning a new result (runRoomSelection merge in
  // updateRoomQty → PW.jsx), the flag is false and the reconcile is skipped,
  // preserving the user's manual Schedule edit. The flag is set to true only
  // inside setRowQty (the user's explicit qty edit on the Schedule tab).
  const prevRoomQtyRef = React.useRef({});
  useEffect(() => {
    if (!configurations || !groups.length) return;
    // Consume the user-initiated flag. Reset it regardless of whether we
    // actually bump anything — one user edit → one reconcile pass at most.
    const initiatedByUser = userInitiatedQtyChangeRef.current;
    userInitiatedQtyChangeRef.current = false;
    // Consume the split-intent set. Any room key in here had a partial
    // first-variant assignment just committed by setRowVariant; the reconcile
    // must not "heal" those rooms back to room.qty.
    const pendingSplitKeys = pendingSplitRoomKeysRef.current;
    pendingSplitRoomKeysRef.current = new Set();
    let next = configurations;
    let changed = false;
    groups.forEach((g) => {
      const k = groupKey(g);
      g.rooms.forEach((r) => {
        // Use room_id as the stable key when available (schema v3).
        // Fall back to room_idx for payloads that pre-date the migration.
        const roomKey = r.room_id != null ? `${k}__${r.room_id}` : `${k}__${r.room_idx}`;
        const prevRoomQty = prevRoomQtyRef.current[roomKey];
        const assigned = roomSums.get(roomKey) || 0;
        // (a) Runtime: qty just changed and assignment still equals OLD qty.
        //     Only proceed when the change was user-initiated (Fix #4): engine-
        //     driven room.qty updates (from runRoomSelection) must not clobber
        //     a manual Schedule edit.
        const runtimeBump =
          initiatedByUser &&
          prevRoomQty !== undefined &&
          prevRoomQty !== r.qty &&
          assigned === prevRoomQty;
        // (b) First-load: never seen before, and assignment is stale.
        //     This path is safe even without the user flag because it only
        //     fires once per room (when prevRoomQty === undefined).
        //     Fix H1-reconcile (part 2): skip only when this ROOM has multiple
        //     variants actively assigned (actual split in progress). Checking
        //     totalForGroup (all variant configs for the group) was too broad —
        //     it suppressed healing even for rooms with a single assignment when
        //     the group happened to have 2+ variant configs defined.
        const assignedVariantsForRoom = next
          .filter((c) => c.key === k)
          .filter((c) => (c.assignments || []).some((a) => matchesRoom(a, r) && a.qty > 0));
        const initialBump =
          prevRoomQty === undefined &&
          assigned > 0 &&
          assigned !== r.qty &&
          assignedVariantsForRoom.length <= 1; // only suppress when room is actively split
        if ((runtimeBump || initialBump) && assigned !== r.qty) {
          // Fix H1-reconcile (part 1): skip if setRowVariant just placed a
          // partial split-intent assignment for this room in the same tick.
          if (pendingSplitKeys.has(roomKey)) {
            // Intentional partial assignment — leave it alone.
          } else {
            const touching = (next.filter((c) => c.key === k) || [])
              .map((c) => ({ c, a: (c.assignments || []).find((a) => matchesRoom(a, r)) }))
              .filter((x) => x.a && x.a.qty > 0);
            if (touching.length === 1) {
              next = setRoomVariantQty(next, k, r, touching[0].c.id, r.qty);
              changed = true;
            }
          }
        }
        prevRoomQtyRef.current[roomKey] = r.qty;
      });
    });
    if (changed) setConfigurations(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, roomSums]);

  // Project-wide totals — used by the variant summary footer.
  const variantSummary = useMemo(() => {
    const m = new Map(); // tag → { tag, name, model, length_ft, qty, codeB, codeA }
    tagged.forEach((v) => {
      const qty = (v.assignments || []).reduce((s, a) => s + (a.qty || 0), 0);
      if (!qty) return;
      const g = groups.find((x) => groupKey(x) === v.key);
      m.set(v.tag, {
        tag: v.tag,
        name: v.name && v.name !== "Default" ? v.name : "",
        model: g?.unit_model || "",
        length_ft: g?.length_ft,
        qty,
      });
    });
    return [...m.values()].sort((a, b) => a.tag.localeCompare(b.tag));
  }, [tagged, groups]);
  const grandTotalQty = variantSummary.reduce((s, r) => s + r.qty, 0);

  if (groups.length === 0) {
    return (
      <div
        className="border-2 border-dashed border-border rounded-lg p-12 text-center"
        data-testid="assignment-empty"
      >
        <ClipboardList className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-foreground mb-1 font-medium">No selections yet</p>
        <p className="text-xs text-muted-foreground">
          Run <strong>Advanced → Perform Selection</strong> and define configurations first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="assignment-tab">
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="h-4 w-4" /> Schedule
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                One row per assignment. Pick a configuration variant to fill the
                Base + Accessory codes. Use <strong>+ Split</strong> to assign
                different variants to units within the same room.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canProjectApplyAll && (
                <Button
                  size="sm" variant="outline"
                  onClick={projectApplyAll}
                  data-testid="assignment-apply-all"
                  title="Auto-assign every room to its default configuration"
                >
                  <Wand2 className="h-4 w-4 mr-1" /> Apply default to all
                </Button>
              )}
              <Button size="sm" onClick={handleSave} disabled={savingBusy}
                data-testid="config-save-file-btn">
                <Save className="h-4 w-4 mr-1" />
                {savingBusy ? "Saving…" : "Save Config File"}
              </Button>
              <Button
                size="sm"
                onClick={handleGenerateConfigReport}
                disabled={configReportBusy || !allAssigned}
                data-testid="generate-config-report-btn"
                className="bg-foreground text-background hover:opacity-90"
                title={!allAssigned
                  ? "Assign every room first"
                  : "Generate Config Report (variant summary + inclusions/exclusions). Saved to project so the Submittal tab can reuse it."}
              >
                <FileText className="h-4 w-4 mr-1" />
                {configReportBusy ? "Generating…" : (configReport ? "Re-generate Config Report" : "Generate Config Report")}
              </Button>
              {configReport && (
                <span
                  className="text-[11px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2 py-1 rounded inline-flex items-center gap-1"
                  data-testid="config-report-status"
                  title={`Generated ${new Date(configReport.generated_at).toLocaleString()} · ${(configReport.size_bytes / 1024).toFixed(0)} KB`}
                >
                  <CheckIcon className="h-3 w-3" />
                  Saved {new Date(configReport.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              )}
              {unassigned > 0 && unassignedRows.some((r) => r.cands.length > 1) && (
                <Button
                  size="sm" variant="outline"
                  onClick={openBulkFill}
                  data-testid="schedule-bulk-fill-btn"
                  className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                >
                  <Sparkles className="h-4 w-4 mr-1" /> Resolve multi-variant assignments
                </Button>
              )}
              <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium border ${
                  allAssigned
                    ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800"
                    : "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800"
                }`}
                data-testid="assignment-status"
              >
                {allAssigned ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
                {allAssigned
                  ? `All ${totalRooms} room(s) assigned`
                  : `${unassigned} of ${totalRooms} unassigned`}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Sticky action bar — mirrors the top card's actions so they stay
          reachable once the user scrolls into a long schedule table. */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border flex flex-wrap items-center gap-2 py-2 px-1 -mx-1">
        {canProjectApplyAll && (
          <Button
            size="sm" variant="outline"
            onClick={projectApplyAll}
            title="Auto-assign every room to its default configuration"
          >
            <Wand2 className="h-4 w-4 mr-1" /> Apply default to all
          </Button>
        )}
        {unassigned > 0 && unassignedRows.some((r) => r.cands.length > 1) && (
          <Button
            size="sm" variant="outline"
            onClick={openBulkFill}
            className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
          >
            <Sparkles className="h-4 w-4 mr-1" /> Resolve variants
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={handleSave} disabled={savingBusy}>
          <Save className="h-4 w-4 mr-1" />
          {savingBusy ? "Saving…" : "Save Config File"}
        </Button>
        <Button
          size="sm"
          onClick={handleGenerateConfigReport}
          disabled={configReportBusy || !allAssigned}
          className="bg-foreground text-background hover:opacity-90"
          title={!allAssigned ? "Assign every room first" : "Generate Config Report"}
        >
          <FileText className="h-4 w-4 mr-1" />
          {configReportBusy ? "Generating…" : (configReport ? "Re-generate Report" : "Config Report")}
        </Button>
        <div className="flex-1" />
        <div
          className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium border ${
            allAssigned
              ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800"
              : "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800"
          }`}
        >
          {allAssigned ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5" />
          )}
          {allAssigned
            ? `All ${totalRooms} assigned`
            : `${unassigned} / ${totalRooms} unassigned`}
        </div>
      </div>

      {/* Per-group sections */}
      <div className="space-y-3">
        {groups.map((g) => {
          const k = groupKey(g);
          const cands = variantsByGroupKey.get(k) || [];
          const groupRows = flatRows.filter((r) => r.groupKey === k);

          return (
            <Card
              key={`${g.unit_model}-${g.length_ft || 0}`}
              className="border-border"
              data-testid={`assignment-group-${g.unit_model}-${g.length_ft || 0}`}
            >
              <CardHeader className="py-3 bg-muted/50 border-b border-border">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <ArrowDownToLine className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-semibold text-sm text-foreground">
                        {unitModelLabel(g, isSI)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {g.qty} unit(s) · {g.rooms.length} room(s) · {cands.length} variant(s)
                      </div>
                    </div>
                  </div>
                  {cands.length > 1 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-xs text-muted-foreground mr-1">Apply to all rooms:</span>
                      {cands.map((v) => (
                        <Button
                          key={v.id} size="sm" variant="outline"
                          className="h-7 text-xs"
                          onClick={() => applyToAllInGroup(g, v.id)}
                          data-testid={`assignment-apply-group-${g.unit_model}-${g.length_ft || 0}-${v.tag}`}
                        >
                          {v.tag}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted border-b border-border">
                    <tr className="text-xs text-muted-foreground">
                      <th className="px-3 py-2 font-semibold text-left w-12">#</th>
                      <th className="px-3 py-2 font-semibold text-left">Room</th>
                      <th className="px-3 py-2 font-semibold text-left">Unit Model</th>
                      <th className="px-3 py-2 font-semibold text-center w-20">QTY</th>
                      <th className="px-3 py-2 font-semibold text-left w-44">Config variant</th>
                      <th className="px-3 py-2 font-semibold text-left">Base config (Code B)</th>
                      <th className="px-3 py-2 font-semibold text-left">Accessories Config (Code A)</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRows.map((row) => {
                      const spec = specForUnitModel(g.unit_model);
                      // unit_model from the group (real per-marketed-model
                      // identity) overrides cfg.unit_model (often undefined
                      // because configs only store the shared spec id). Without
                      // this, Model C1 codes lose the "1" suffix.
                      const baseCode = row.variant && spec
                        ? buildBaseCode(spec, row.variant.base, row.variant.extras, { unitModel: g.unit_model || row.variant.unit_model }) : "";
                      const accCode = row.variant && spec
                        ? buildAccessoryCode(spec, row.variant.accessory, row.variant.extras) : "";
                      const sumKey = roomSumKey(row);
                      const sum = roomSums.get(sumKey) || 0;
                      const ok = sum === row.room.qty;
                      const remaining = row.room.qty - sum;
                      // canSplit: show the "+ Split" button only when:
                      //   1) this is the lead sub-row for the room
                      //   2) a variant is already picked (prevents auto-assign-from-empty)
                      //   3) there are at least 2 variants to split across
                      //   4) there is still unassigned qty remaining
                      //   5) fewer variant rows than available candidates (a free slot exists)
                      const assignedRowsForRoom = groupRows.filter((r) => r.room === row.room && r.variant);
                      const canSplit = row.isFirstSubRow
                        && row.variant !== null
                        && row.cands.length > 1
                        && remaining > 0
                        && row.cands.length > assignedRowsForRoom.length;
                      // canAddVariant: offer a shortcut when exactly one variant exists
                      // and room.qty > 1 — cloning the variant makes split reachable
                      // without navigating back to Configurator.
                      const canAddVariant = row.isFirstSubRow
                        && row.room.qty > 1
                        && row.cands.length === 1;
                      return (
                        <tr
                          key={`${row.room.room_idx}-${row.subRow}`}
                          className="border-t border-slate-100"
                          data-testid={`schedule-row-${row.room.room_idx}-${row.subRow}`}
                        >
                          <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                            {row.isFirstSubRow ? row.num : ""}
                          </td>
                          <td className="px-3 py-2">
                            {row.isFirstSubRow ? (
                              <div className="font-medium text-foreground">
                                {row.room.room_name || `Room ${row.room.room_idx + 1}`}
                                {!ok && (
                                  <div className="text-[10px] text-rose-700 mt-0.5">
                                    Σ {sum} / {row.room.qty}
                                    <AlertTriangle className="h-3 w-3 inline ml-1" />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">↳</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-foreground">
                            {row.isFirstSubRow ? unitModelLabel(g, isSI) : ""}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {row.variant ? (
                              <QtyInput
                                value={row.qty}
                                max={row.room.qty}
                                onCommit={(v) => setRowQty(row, v)}
                                className="h-7 w-16 text-center text-sm mx-auto"
                                testid={`schedule-qty-${row.room.room_idx}-${row.subRow}`}
                              />
                            ) : (
                              <span className="font-mono text-foreground">{row.room.qty}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Select
                              value={row.variant?.id || ""}
                              onValueChange={(id) => setRowVariant(row, id)}
                            >
                              <SelectTrigger
                                className="h-8 text-xs"
                                data-testid={`schedule-variant-${row.room.room_idx}-${row.subRow}`}
                              >
                                <SelectValue placeholder={cands.length === 0 ? "no variants" : "select…"} />
                              </SelectTrigger>
                              <SelectContent>
                                {cands.map((v) => (
                                  <SelectItem key={v.id} value={v.id}>
                                    {v.tag}{v.name && v.name !== "Default" ? ` — ${v.name}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2 font-mono text-[10px] text-amber-800 dark:text-amber-300 break-all max-w-xs"
                              data-testid={`schedule-baseB-${row.room.room_idx}-${row.subRow}`}>
                            {baseCode || <span className="text-muted-foreground italic">—</span>}
                          </td>
                          <td className="px-3 py-2 font-mono text-[10px] text-emerald-800 dark:text-emerald-300 break-all max-w-xs"
                              data-testid={`schedule-accA-${row.room.room_idx}-${row.subRow}`}>
                            {accCode || <span className="text-muted-foreground italic">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {row.subRow > 0 && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => removeRow(row)}
                                className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700"
                                data-testid={`schedule-remove-${row.room.room_idx}-${row.subRow}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canSplit && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => addSplitRow(row)}
                                className="h-7 px-2 text-xs"
                                data-testid={`schedule-split-${row.room.room_idx}`}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" /> Split
                              </Button>
                            )}
                            {canAddVariant && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => cloneOnlyVariant(g)}
                                className="h-7 px-2 text-xs text-indigo-600 hover:text-indigo-700"
                                title="Clone this variant so you can split units across two configurations"
                                data-testid={`schedule-add-variant-${row.room.room_idx}`}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" /> Add variant
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted border-t border-border">
                    {(() => {
                      const k = groupKey(g);
                      const totalAssigned = g.rooms.reduce((s, r) => {
                        const key = r.room_id != null ? `${k}__${r.room_id}` : `${k}__${r.room_idx}`;
                        return s + (roomSums.get(key) || 0);
                      }, 0);
                      const totalRoom = g.rooms.reduce((s, r) => s + (r.qty || 0), 0);
                      return (
                        <tr className="text-xs font-semibold text-foreground">
                          <td colSpan={3} className="px-3 py-2 text-right">Group subtotal</td>
                          <td className="px-3 py-2 text-center" data-testid={`schedule-subtotal-${g.unit_model}-${g.length_ft || 0}`}>
                            {totalAssigned === totalRoom ? totalRoom : `${totalAssigned} / ${totalRoom}`}
                          </td>
                          <td colSpan={4} className="px-3 py-2 text-left text-muted-foreground font-normal">
                            {g.rooms.length} room{g.rooms.length !== 1 ? "s" : ""}
                            {totalAssigned !== totalRoom && (
                              <span className="ml-2 text-amber-700 dark:text-amber-400">
                                · {totalRoom - totalAssigned} unassigned
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Project-wide variant summary — counts per Tag with running total */}
      {variantSummary.length > 0 && (
        <Card data-testid="schedule-variant-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Variant Summary</span>
              <span className="text-sm font-mono text-emerald-700 dark:text-emerald-400">
                Total: <strong>{grandTotalQty}</strong> unit{grandTotalQty !== 1 ? "s" : ""}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border">
                <tr className="text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-semibold text-left w-20">Tag</th>
                  <th className="px-3 py-2 font-semibold text-left">Variant Name</th>
                  <th className="px-3 py-2 font-semibold text-left">Unit Model</th>
                  <th className="px-3 py-2 font-semibold text-center w-24">QTY</th>
                </tr>
              </thead>
              <tbody>
                {variantSummary.map((v) => (
                  <tr key={v.tag} className="border-t border-slate-200">
                    <td className="px-3 py-2 font-bold">{v.tag}</td>
                    <td className="px-3 py-2 text-foreground">{v.name || <span className="text-muted-foreground italic">—</span>}</td>
                    <td className="px-3 py-2 text-foreground">
                      {v.model}{v.length_ft != null && !isLengthAgnostic(v.model) ? ` · ${isSI ? `${Math.round(parseFloat(v.length_ft) * 300)} mm` : `${v.length_ft} ft`}` : ""}
                    </td>
                    <td className="px-3 py-2 text-center font-mono font-semibold">{v.qty}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-emerald-50 dark:bg-emerald-950/40 border-t-2 border-emerald-300 dark:border-emerald-700">
                <tr className="text-sm font-bold text-emerald-900 dark:text-emerald-300">
                  <td colSpan={3} className="px-3 py-2 text-right">Total Units</td>
                  <td className="px-3 py-2 text-center font-mono" data-testid="schedule-variant-summary-total">{grandTotalQty}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}

      {!allAssigned && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {unassigned} room(s) still need a configuration. Submittal generation
          will be blocked until every room is fully assigned.
        </div>
      )}

      {/* Resolve variant assignments dialog.
          Groups unassigned multi-variant rooms by unit-size group.
          Each group section has a "Set all" shortcut dropdown at the top,
          then per-room dropdowns below. */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resolve variant assignments</DialogTitle>
            <DialogDescription>
              Some rooms have multiple variant options. Pick one variant per
              room, or use "Set all" to apply the same variant to every room
              in a group at once.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto space-y-4 pr-1">
            {multiVariantGroupsForModal.map(({ gk, group, rows: groupRows }) => {
              if (!group) return null;
              const cands = groupRows[0]?.cands || [];
              // Determine whether all rows in this group currently share the same
              // variant selection — used to populate the "Set all" trigger label.
              const allSameVariantId = groupRows.every(
                (r) => bulkSelections[roomBulkKey(r)] === bulkSelections[roomBulkKey(groupRows[0])]
              )
                ? bulkSelections[roomBulkKey(groupRows[0])]
                : "";

              return (
                <div key={gk} className="border border-border rounded">
                  {/* Group header with "Set all" shortcut */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2 bg-muted/50 border-b border-border">
                    <span className="font-semibold text-sm text-foreground">
                      {unitModelLabel(group, isSI)}
                      <span className="font-normal text-muted-foreground ml-2 text-xs">
                        ({groupRows.length} room{groupRows.length !== 1 ? "s" : ""})
                      </span>
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Set all</span>
                      <Select
                        value={allSameVariantId}
                        onValueChange={(variantId) => {
                          setBulkSelections((prev) => {
                            const next = { ...prev };
                            groupRows.forEach((r) => {
                              next[roomBulkKey(r)] = variantId;
                            });
                            return next;
                          });
                        }}
                      >
                        <SelectTrigger
                          className="h-7 text-xs w-36"
                          data-testid={`bulk-set-all-${gk}`}
                        >
                          <SelectValue placeholder="pick variant" />
                        </SelectTrigger>
                        <SelectContent>
                          {cands.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.tag}{v.name && v.name !== "Default" ? ` — ${v.name}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Per-room rows */}
                  <div className="divide-y divide-border">
                    {groupRows.map((row) => {
                      const key = roomBulkKey(row);
                      const sel = bulkSelections[key] || "";
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                        >
                          <span className="text-foreground min-w-0 truncate">
                            {row.room.room_name || `Room ${row.room.room_idx + 1}`}
                          </span>
                          <Select
                            value={sel}
                            onValueChange={(variantId) =>
                              setBulkSelections((p) => ({ ...p, [key]: variantId }))
                            }
                          >
                            <SelectTrigger
                              className="h-7 text-xs w-36 shrink-0"
                              data-testid={`bulk-select-${key}`}
                            >
                              <SelectValue placeholder="select…" />
                            </SelectTrigger>
                            <SelectContent>
                              {row.cands.map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.tag}{v.name && v.name !== "Default" ? ` — ${v.name}` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={applyBulkFill} data-testid="bulk-apply-btn">
              Apply to {Object.keys(bulkSelections).length} room(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* (Export dialog removed — Tab 7 now exports the full Submittal
          Documents directly via generateSubmittalPDF, matching the Summary
          tab's "Export Detailed Selection".) */}
    </div>
  );
}
